import { 
    PORTFOLIO_CONFIG, 
    SPECIAL_PORTFOLIOS, 
    PORTFOLIO_ICONS, 
    APP_CONSTANTS 
  } from '@/constants/portfolio.constants';
  
  /**
   * Portfolio business logic service
   * Handles all portfolio-related business rules and data transformations
   */
  export class PortfolioService {
    /**
     * Get portfolio configuration by ID
     * @param {number|string} portfolioId 
     * @returns {object} Portfolio configuration
     */
    static getConfig(portfolioId) {
      return PORTFOLIO_CONFIG[portfolioId] || PORTFOLIO_CONFIG.default;
    }
  
    /**
     * Get portfolio CSS classes
     * @param {number|string} portfolioId 
     * @returns {object} CSS classes object
     */
    static getClasses(portfolioId) {
      const config = this.getConfig(portfolioId);
      return config.classes;
    }
  
    /**
     * Get portfolio icon for special portfolios
     * @param {string} portfolioName 
     * @returns {string} Icon path
     */
    static getIcon(portfolioName) {
      return PORTFOLIO_ICONS[portfolioName] || PORTFOLIO_ICONS.innovation;
    }
  
    /**
     * Check if portfolio is special (requires SuperMarker)
     * @param {string} portfolioName 
     * @returns {boolean}
     */
    static isSpecialPortfolio(portfolioName) {
      return SPECIAL_PORTFOLIOS.has(portfolioName);
    }
  
    /**
     * Determine if portfolio is currently active
     * @param {number|string} portfolioId 
     * @param {object} activePortfolio 
     * @returns {boolean}
     */
    static isActivePortfolio(portfolioId, activePortfolio) {
      if (!activePortfolio || portfolioId === APP_CONSTANTS.DEFAULT_PORTFOLIO_ID) {
        return true;
      }
      
      const activePortfolioName = activePortfolio?.name?.toLowerCase();
      return activePortfolioName === APP_CONSTANTS.ALL_PORTFOLIO || 
             portfolioId === activePortfolio.id;
    }
  
    /**
     * Check if value drivers are active
     * @param {Array} drivers 
     * @param {Array} activeValueDrivers 
     * @returns {boolean}
     */
    static isActiveValueDriver(drivers = [], activeValueDrivers = []) {
      if (!activeValueDrivers.length) return true;
      const driverIds = Array.isArray(drivers) ? drivers : [];
      if (driverIds.length === 0) return true;
      return activeValueDrivers.some(driver =>
        driverIds.some(d => d == driver.id)
      );
    }
  
    /**
     * Check if hotspot layer matches active layer (handles solutions/key-solutions/layer_id 3)
     */
    static isLayerMatch(layer, activeLayerId) {
      if (layer == null || activeLayerId == null) return false;
      if (layer === activeLayerId) return true;
      const isSolutions = (id) =>
        id === APP_CONSTANTS.SOLUTIONS_LAYER || id === 'solutions' || id === 3;
      return isSolutions(layer) && isSolutions(activeLayerId);
    }

    /**
     * Determine hotspot visibility based on business rules
     * @param {object} params - Parameters object
     * @returns {boolean}
     */
    static isHotspotVisible({ 
      layer, 
      activeLayer, 
      portfolio, 
      activePortfolio, 
      drivers, 
      activeValueDrivers 
    }) {
      const activeId = activeLayer?.id;
      const isActiveLayer = this.isLayerMatch(layer, activeId);
      const isSolutionsLayer = activeId === APP_CONSTANTS.SOLUTIONS_LAYER || activeId === 'solutions';
      
      if (!isActiveLayer) return false;
      
      if (isSolutionsLayer) {
        const isPortfolioActive = this.isActivePortfolio(portfolio, activePortfolio);
        const isDriverActive = this.isActiveValueDriver(drivers, activeValueDrivers);
        return isPortfolioActive && isDriverActive;
      }
      
      return true;
    }
  
    /**
     * Get thumbnail source with fallback
     * @param {string} thumbnail 
     * @param {boolean} imageError 
     * @returns {string}
     */
    static getThumbnailSrc(thumbnail, imageError = false) {
      if (imageError || !thumbnail || thumbnail === 'all') {
        return APP_CONSTANTS.FALLBACK_IMAGE;
      }
      return thumbnail;
    }
  }